import { BaseTool, IToolContext, IToolManifest } from '../../../core/tools/interfaces/ITool';
import type { ICommand } from '../../../core/commands/ICommand';
import { CompositeCommand } from '../../../core/commands/ICommand';
import { CreateNodeCommand } from '../../../core/commands/CreateNodeCommand';
import { CreateEdgeCommand } from '../../../core/commands/CreateEdgeCommand';
import { useDiagramStore } from '../../../stores/diagramStore';
import type { Node } from '../../../core/models/scene';
import i18n from '../../../i18n/config';
import { bindGenogramToolName } from './utils/toolNameI18n';

const PERSON_SIZE = 60;
const SPACING = 120;

export class AddChildToCoupleTool extends BaseTool {
  manifest: IToolManifest = {
    id: 'genogram-add-child-to-couple',
    name: i18n.t('genogram:tools.addChildToCouple', { defaultValue: 'Add Child to Couple' }) as string,
    icon: '👨‍👩‍👧',
    category: 'create',
    description: 'Add a child to a couple (two people selected)',
    version: '1.0.0',
    supportedDiagramTypes: ['genogram' as any],
    requiresSelection: true,
    minSelection: 2,
    maxSelection: 2,
    ui: {
      toolbar: false,
      contextMenu: false,
      propertyPanel: false,
      sidebar: false,
      floatingPanel: true,
      statusBar: false
    },
    applicability: {
      contextTypes: ['person'],
      diagramTypes: ['genogram'],
    },
    capabilities: {
      modifiesShapes: false,
      createsShapes: true,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: true,
      deletesConnectors: false,
      requiresUserInput: false,
      supportsMultiSelect: true,
      supportsBatchOperations: false
    },
    shortcuts: [
      { key: '2', description: 'Add child to couple (when two people selected)' }
    ]
  };

  constructor() {
    super();
    bindGenogramToolName(this.manifest, 'tools.addChildToCouple', 'Add Child to Couple');
  }

  canExecute(context: IToolContext): boolean {
    if (!super.canExecute(context)) return false;

    // Check that selection is exactly 2 people
    const summary = context.selectionSummary;
    return summary?.contextType === 'person' && context.selection.size === 2;
  }

  execute(): ICommand | null {
    if (!this.context) return null;

    const [parentId1, parentId2] = Array.from(this.context.selection);
    const state = useDiagramStore.getState();
    const parent1 = state.nodes.find(n => n.id === parentId1);
    const parent2 = state.nodes.find(n => n.id === parentId2);

    if (!parent1 || !parent2) return null;

    const pos1 = { x: parent1.x, y: parent1.y };
    const pos2 = { x: parent2.x, y: parent2.y };
    const size1 = {
      width: (parent1 as any).w ?? (parent1 as any).width ?? PERSON_SIZE,
      height: (parent1 as any).h ?? (parent1 as any).height ?? PERSON_SIZE,
    } as { width: number; height: number };
    const size2 = {
      width: (parent2 as any).w ?? (parent2 as any).width ?? PERSON_SIZE,
      height: (parent2 as any).h ?? (parent2 as any).height ?? PERSON_SIZE,
    } as { width: number; height: number };

    // Calculate midpoint between the two parents
    const midX = (pos1.x + size1.width / 2 + pos2.x + size2.width / 2) / 2;
    const maxY = Math.max(pos1.y + size1.height, pos2.y + size2.height);

    // Position child below and centered between parents
    const newNodeId = `person-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newSize = {
      width: Math.max(size1.width, size2.width),
      height: Math.max(size1.height, size2.height),
    };
    const newNode: Node = {
      id: newNodeId,
      type: 'person',
      name: i18n.t('genogram:relationships.child', { defaultValue: 'Child' }) as string,
      sex: 'unknown',
      x: midX - newSize.width / 2,
      y: maxY + SPACING,
      w: newSize.width,
      h: newSize.height,
      width: newSize.width,
      height: newSize.height,
      category: 'genogram',
    } as Node;

    const nodeCommand = new CreateNodeCommand(newNode);

    // Create edges from both parents to child
    const edge1Command = new CreateEdgeCommand({
      type: 'child',
      from: parentId1,
      to: newNodeId,
      category: 'genogram',
      fromAnchor: 'bottom',
      toAnchor: 'top',
      attrs: { __captionHidden: true },
    });

    const edge2Command = new CreateEdgeCommand({
      type: 'child',
      from: parentId2,
      to: newNodeId,
      category: 'genogram',
      fromAnchor: 'bottom',
      toAnchor: 'top',
      attrs: { __captionHidden: true },
    });

    return new CompositeCommand('Add Child to Couple', [nodeCommand, edge1Command, edge2Command]);
  }
}
